import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { FullPageMessage } from '@/components/FullPageMessage'
import { Button, ErrorNote } from '@/components/ui'

const COPY = {
  invite: {
    title: 'Accept your invitation',
    body: 'Continue to set a password and join your organisation on the portal.',
  },
  recovery: {
    title: 'Reset your password',
    body: 'Continue to choose a new password.',
  },
} as const

type EmailLinkType = keyof typeof COPY

/**
 * Where invitation and password-reset emails land (the links are built by
 * the send-email-hook Edge Function).
 *
 * The one-time token is spent only when the person presses Continue. Mail
 * scanners fetch every link in a message but do not click buttons, so a
 * scanner visiting this page leaves the token intact. That is the whole
 * reason this is a button and not an automatic redirect: do not "improve"
 * it into a useEffect.
 */
export function AuthConfirm() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const tokenHash = params.get('token_hash')
  const type = params.get('type')

  if (!tokenHash || (type !== 'invite' && type !== 'recovery')) {
    return (
      <FullPageMessage
        title="That link is incomplete"
        body="Open the link from your email again. If you copied it, check the whole address came across."
      >
        <Button onClick={() => navigate('/sign-in', { replace: true })}>Back to sign in</Button>
      </FullPageMessage>
    )
  }

  const linkType: EmailLinkType = type

  async function confirm() {
    setBusy(true)
    setError(null)
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash!, type: linkType })
    setBusy(false)
    if (error) {
      setError(
        linkType === 'invite'
          ? 'That invitation link has already been used or has expired. Ask whoever invited you to send a fresh one.'
          : 'That reset link has already been used or has expired. Request a new one from the sign-in page.',
      )
      return
    }
    navigate('/set-password', { replace: true })
  }

  return (
    <FullPageMessage title={COPY[linkType].title} body={COPY[linkType].body}>
      {error && <div className="mb-4 text-left"><ErrorNote>{error}</ErrorNote></div>}
      <Button onClick={confirm} disabled={busy}>
        {busy ? 'One moment…' : 'Continue'}
      </Button>
      {error && (
        <p className="mt-4 text-sm">
          <Button variant="quiet" onClick={() => navigate('/sign-in', { replace: true })}>
            Back to sign in
          </Button>
        </p>
      )}
    </FullPageMessage>
  )
}
